import { Controller } from "@hotwired/stimulus"

// The product page's section bar: tabs that jump to the page's sections, plus
// Compare and Share. Once the in-page bar scrolls off the top, a pinned copy
// that also names the product takes over, and the tab for the section in view
// stays highlighted in both.
export default class extends Controller {
  static targets = ["inline", "pinned", "tab"]
  static values = { compareButtonId: String }

  connect() {
    this.observer = new IntersectionObserver(([entry]) => {
      this.pin(!entry.isIntersecting && entry.boundingClientRect.top < 0)
    })
    this.observer.observe(this.inlineTarget)

    this.onScroll = () => {
      if (this.frame) return
      this.frame = requestAnimationFrame(() => {
        this.frame = null
        this.highlight()
      })
    }
    window.addEventListener("scroll", this.onScroll, { passive: true })
    this.highlight()
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("scroll", this.onScroll)
    cancelAnimationFrame(this.frame)
  }

  jump(event) {
    event.preventDefault()
    const section = document.getElementById(event.currentTarget.dataset.section)
    const top = section ? section.getBoundingClientRect().top + window.scrollY - this.offset : 0
    window.scrollTo({ top, behavior: "smooth" })
  }

  // The pinned bar's Compare opens the same drawer as the in-page button
  compare() {
    document.getElementById(this.compareButtonIdValue)?.click()
  }

  pin(pinned) {
    this.pinnedTarget.toggleAttribute("data-visible", pinned)
    this.pinnedTarget.inert = !pinned
  }

  highlight() {
    let current = ""
    for (const id of this.sectionIds) {
      const section = document.getElementById(id)
      if (section && section.getBoundingClientRect().top <= this.offset + 1) current = id
    }
    this.activate(current)
  }

  activate(id) {
    if (id === this.current) return
    this.current = id

    this.tabTargets.forEach((tab) => {
      if (tab.dataset.section !== id) {
        tab.removeAttribute("aria-current")
        return
      }

      tab.setAttribute("aria-current", "location")
      // On narrow screens the tabs scroll sideways; keep the active one in view
      const nav = tab.parentElement
      if (nav.scrollWidth > nav.clientWidth) {
        nav.scrollTo({ left: tab.offsetLeft - 16, behavior: "smooth" })
      }
    })
  }

  get sectionIds() {
    return [...new Set(this.tabTargets.map((tab) => tab.dataset.section).filter(Boolean))]
  }

  // Sections land just below the pinned bar
  get offset() {
    return this.pinnedTarget.offsetHeight + 12
  }
}
